import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useDealCreationFieldSettingsQuery,
  useUpdateDealCreationFieldsMutation,
  DealCreationFieldKindEnum,
  CrmIngestionPlatform,
} from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";
import { readPreviewCustomFields, subscribePreviewCustomFields } from "@/lib/preview-custom-fields";

export interface DealField {
  id?: string;
  kind: "STANDARD" | "CUSTOM";
  standardKey?: string;
  crmFieldId?: string;
  crmLabel?: string;
  required: boolean;
  position: number;
}

export interface CustomFieldOption {
  id: string;
  label: string;
  name: string;
}

/**
 * The standard fields the New Deal form can show, as KEYS.
 *
 * `key` is the wire value the backend stores and must never be translated;
 * `labelKey` is what the user reads. They are deliberately a separate section
 * from `fields.deals` — this form says "Deal Stage" where the grid column says
 * "Stage", and `fields.*` is pinned to the grid columns by a parity test.
 */
const STANDARD_FIELDS_BASE = [
  { key: "name", labelKey: "dealFields.name" },
  { key: "company", labelKey: "dealFields.company" },
  { key: "amount", labelKey: "dealFields.amount" },
  { key: "stage", labelKey: "dealFields.stage" },
  { key: "close_date", labelKey: "dealFields.closeDate" },
  { key: "owner", labelKey: "dealFields.owner" },
  { key: "contacts", labelKey: "dealFields.contacts" },
  { key: "last_email", labelKey: "dealFields.lastEmail" },
  { key: "last_meeting", labelKey: "dealFields.lastMeeting" },
  { key: "next_meeting", labelKey: "dealFields.nextMeeting" },
  { key: "billing", labelKey: "dealFields.billing" },
  { key: "created_date", labelKey: "dealFields.createdDate" },
  { key: "created_by", labelKey: "dealFields.createdBy" },
  { key: "age", labelKey: "dealFields.age" },
  { key: "time_in_stage", labelKey: "dealFields.timeInStage" },
];

// Default-on fields when a workspace first lands on the New Deal Fields
// settings page. The remaining standard fields stay discoverable via the
// "Add Field" picker — they're just not enabled out of the box.
const PREVIEW_DEFAULT_KEYS = ["name", "company", "amount", "stage", "close_date", "owner"];

const PREVIEW_FIELDS_BASE: DealField[] = PREVIEW_DEFAULT_KEYS.map((key, idx) => ({
  id: `p${idx + 1}`,
  kind: "STANDARD",
  standardKey: key,
  required: true,
  position: idx,
}));

/* Simulated WORKSPACE custom fields for the Lovable preview: in production
   these labels come from the workspace's own field definitions, which are user
   data and never translated. They stand in for that data, so they stay English
   for the same reason. */
/* eslint-disable coffee-i18n/no-config-object-copy */
const PREVIEW_CUSTOM_FIELDS: CustomFieldOption[] = [
  { id: "cf1", label: "Decision Criteria", name: "meddpicc_decision_criteria" },
  { id: "cf2", label: "Champion", name: "meddpicc_champion" },
  { id: "cf3", label: "Competition", name: "competition" },
  { id: "cf4", label: "Next Steps", name: "next_steps" },
  { id: "cf5", label: "Pain Points", name: "pain_points" },
  { id: "cf6", label: "Metrics", name: "meddpicc_metrics" },
  { id: "cf7", label: "Economic Buyer", name: "meddpicc_economic_buyer" },
  { id: "cf8", label: "Decision Process", name: "meddpicc_decision_process" },
  { id: "cf9", label: "Paper Process", name: "meddpicc_paper_process" },
  { id: "cf10", label: "Implicate the Pain", name: "meddpicc_implicate_pain" },
];
/* eslint-enable coffee-i18n/no-config-object-copy */

interface DealFieldsContextValue {
  fields: DealField[];
  loading: boolean;
  error: string | null;
  updating: boolean;
  customFieldOptions: CustomFieldOption[];
  /** Pipeline whose field config is loaded. null = the workspace default,
   *  which the primary pipeline and any uncustomized pipeline inherit. */
  pipelineId: string | null;
  setPipelineId: (id: string | null) => void;
  getFieldLabel: (field: DealField) => string;
  addField: (kind: "STANDARD" | "CUSTOM", key: string) => void;
  removeField: (fieldId: string) => void;
  toggleRequired: (fieldId: string) => void;
  reorderFields: (fromIndex: number, toIndex: number) => void;
  save: () => Promise<{ success: boolean; error?: string }>;
  getAvailableFields: () => { standard: { key: string; label: string }[]; custom: CustomFieldOption[] };
  refetch: () => void;
}

const DealFieldsContext = createContext<DealFieldsContextValue | null>(null);

export function DealFieldsProvider({ children }: { children: ReactNode }) {
  const [fields, setFields] = useState<DealField[]>([]);
  const [customFieldOptions, setCustomFieldOptions] = useState<CustomFieldOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pipelineId, setPipelineId] = useState<string | null>(null);

  const { t } = useTranslation();
  // Resolved here rather than on the constant: `t` at module scope would pin
  // every label to the language the tab was opened in.
  const STANDARD_FIELDS = useMemo(
    () => STANDARD_FIELDS_BASE.map(field => ({ key: field.key, label: t(field.labelKey) })),
    [t],
  );
  const PREVIEW_FIELDS = PREVIEW_FIELDS_BASE;

  const workspaceId = useWorkspaceId();
  const preview = isLovablePreview();


  const { data, loading: queryLoading, error: queryError, refetch } = useDealCreationFieldSettingsQuery({
    skip: !workspaceId || preview,
    variables: {
      workspaceId: workspaceId || "",
      workspaceIdInt: workspaceId ? parseInt(workspaceId) : null,
      platform: CrmIngestionPlatform.Coffee,
      first: 200,
      pipelineId,
    },
    fetchPolicy: "network-only",
  });

  const [updateMutation, { loading: updating }] = useUpdateDealCreationFieldsMutation();

  useEffect(() => {
    if (preview) {
      setFields(PREVIEW_FIELDS);
      const refresh = () => {
        const extra = readPreviewCustomFields(workspaceId, "Deal").map(e => ({
          id: e.id,
          label: e.label,
          name: e.name,
        }));
        // De-dupe by id; runtime-added entries take precedence
        const seen = new Set(extra.map(e => e.id));
        setCustomFieldOptions([...extra, ...PREVIEW_CUSTOM_FIELDS.filter(f => !seen.has(f.id))]);
      };
      refresh();
      const unsub = subscribePreviewCustomFields(refresh);
      setLoading(false);
      return unsub;
    }

    if (queryError) {
      console.error("[DealFieldsContext] GraphQL query error:", queryError);
      setError(queryError.message || "Failed to load deal fields");
      setFields([]);
      setLoading(false);
      return;
    }

    if (!data) {
      setLoading(queryLoading);
      return;
    }

    try {
      const sorted = [...(data.dealCreationFields || [])].sort(
        (a, b) => (a?.position ?? 0) - (b?.position ?? 0)
      );

      const mappedFields = sorted.map((f) => ({
        id: f?.id,
        kind: (f?.kind as "STANDARD" | "CUSTOM") || "STANDARD",
        standardKey: f?.standardKey || undefined,
        crmFieldId: f?.crmField?.id || undefined,
        crmLabel: f?.crmField?.crmLabel || undefined,
        required: f?.required ?? false,
        position: f?.position ?? 0,
      }));

      // Filter out any pipeline fields from backend config (pipeline is handled automatically by AddDealDialog)
      const finalFields = mappedFields
        .filter(f => !(f.kind === 'STANDARD' && f.standardKey === 'pipeline'))
        .map((f, i) => ({ ...f, position: i }));

      setFields(finalFields);

      const ingestionEdges = data.crmIngestionFields?.edges ?? [];
      const customOptions = ingestionEdges
        .map((e) => e?.node)
        .filter((f) => f && f.objectType === "DEAL" && !f.isStandardField)
        .map((f) => ({
          id: f!.id!,
          label: f!.crmLabel || f!.crmName || "Unknown",
          name: f!.crmName || "",
        }));

      setCustomFieldOptions(customOptions);
      setError(null);
    } catch (err) {
      console.error("Failed to load deal fields:", err);
      setError("Failed to load deal fields");
    } finally {
      setLoading(false);
    }
  }, [data, queryLoading, preview, workspaceId]);

  const getFieldLabel = useCallback((field: DealField): string => {
    if (field.kind === "STANDARD") {
      const std = STANDARD_FIELDS.find((f) => f.key === field.standardKey);
      return std?.label || field.standardKey || "Unknown";
    }
    return field.crmLabel || "Custom Field";
  }, [STANDARD_FIELDS]);

  const addField = useCallback(
    (kind: "STANDARD" | "CUSTOM", key: string) => {
      const newField: DealField = {
        id: preview ? `p${Date.now()}` : undefined,
        kind,
        standardKey: kind === "STANDARD" ? key : undefined,
        crmFieldId: kind === "CUSTOM" ? key : undefined,
        crmLabel: kind === "CUSTOM" ? customFieldOptions.find((o) => o.id === key)?.label : undefined,
        required: false,
        position: fields.length,
      };
      setFields((prev) => [...prev, newField]);
    },
    [preview, fields.length, customFieldOptions]
  );

  const removeField = useCallback((fieldId: string) => {
    setFields((prev) =>
      prev
        .filter((f) => f.id !== fieldId)
        .map((f, idx) => ({ ...f, position: idx }))
    );
  }, []);

  const toggleRequired = useCallback((fieldId: string) => {
    setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, required: !f.required } : f)));
  }, []);

  const reorderFields = useCallback((fromIndex: number, toIndex: number) => {
    setFields((prev) => {
      const newFields = [...prev];
      const [removed] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, removed);
      return newFields.map((f, idx) => ({ ...f, position: idx }));
    });
  }, []);

  const save = useCallback(async () => {
    if (preview) return { success: true };
    if (!workspaceId) return { success: false, error: "No workspace ID" };

    try {
      const payload = fields.map((f, idx) => ({
        id: f.id || null,
        kind: f.kind === "STANDARD" ? DealCreationFieldKindEnum.Standard : DealCreationFieldKindEnum.Custom,
        standardKey: f.kind === "STANDARD" ? f.standardKey || null : null,
        crmFieldId: f.kind === "CUSTOM" ? f.crmFieldId || null : null,
        required: f.required,
        position: idx,
      }));

      const result = await updateMutation({ variables: { workspaceId, pipelineId, fields: payload } });
      if (!result.data?.updateDealCreationFields?.ok) {
        return { success: false, error: "Backend rejected the update" };
      }
      await refetch();
      return { success: true };
    } catch (err) {
      console.error("Failed to save deal fields:", err);
      return { success: false, error: "Failed to save changes" };
    }
  }, [preview, workspaceId, pipelineId, fields, updateMutation, refetch]);

  const getAvailableFields = useCallback(() => {
    const usedStandardKeys = new Set(fields.filter((f) => f.kind === "STANDARD").map((f) => f.standardKey));
    const usedCustomIds = new Set(fields.filter((f) => f.kind === "CUSTOM").map((f) => f.crmFieldId));

    return {
      standard: STANDARD_FIELDS.filter((f) => !usedStandardKeys.has(f.key)),
      custom: customFieldOptions.filter((f) => !usedCustomIds.has(f.id)),
    };
  }, [fields, customFieldOptions, STANDARD_FIELDS]);

  return (
    <DealFieldsContext.Provider
      value={{
        fields,
        loading,
        error,
        updating,
        customFieldOptions,
        pipelineId,
        setPipelineId,
        getFieldLabel,
        addField,
        removeField,
        toggleRequired,
        reorderFields,
        save,
        getAvailableFields,
        refetch: () => refetch(),
      }}
    >
      {children}
    </DealFieldsContext.Provider>
  );
}

export function useDealFieldsContext() {
  const ctx = useContext(DealFieldsContext);
  if (!ctx) throw new Error("useDealFieldsContext must be used within DealFieldsProvider");
  return ctx;
}
